/**
 * SOPs Hooks
 *
 * TanStack Query hooks for SOP CRUD operations including:
 * - Basic CRUD with pagination
 * - PDF upload with presigned URLs
 * - AI-powered step parsing
 * - Step management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  SopListItem,
  SopLookup,
  SopDetail,
  SopDetailSection,
  SopSectionTask,
  SopTaskStep,
  SopUploadResponse,
  CreateSop,
  UpdateSop,
  UpdateSopSteps,
  ParseSopResponse,
  SopListQuery,
  SopStepInput,
  SopProcessingJobResponse,
  SopProcessingStatusResponse,
} from '@guild/shared';

// Re-export types for components to use
export type { SopDetailSection, SopSectionTask, SopTaskStep };

// Query keys
export const sopKeys = {
  all: ['sops'] as const,
  lists: () => [...sopKeys.all, 'list'] as const,
  list: (filters: SopListQuery & { page?: number; limit?: number }) =>
    [...sopKeys.lists(), filters] as const,
  lookup: () => [...sopKeys.all, 'lookup'] as const,
  sectionsLookup: (id: string) => [...sopKeys.all, 'sections-lookup', id] as const,
  details: () => [...sopKeys.all, 'detail'] as const,
  detail: (id: string) => [...sopKeys.details(), id] as const,
  processingStatus: (id: string) => [...sopKeys.all, 'processing', id] as const,
};

/**
 * A single Job (DB: sopSections) as returned by GET /sops/:id/sections/lookup.
 * Declared here rather than in @guild/shared because the endpoint is the only
 * producer and this hook the only consumer.
 */
export interface SopSectionLookupItem {
  id: string;
  sectionNumber: number;
  title: string;
  /** Steps reachable via this Job's tasks. 0 = cannot be scored. */
  stepCount: number;
}

// Extended SOP detail with download URL and hierarchical structure
// Note: The base SopDetail type already includes sections, jobCount, taskCount, stepCount, processingStatus, pageCount
interface SopDetailWithUrl extends SopDetail {
  downloadUrl: string;
}

// Response types for paginated data
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// LIST QUERIES
// ============================================

/**
 * Fetch SOPs list with pagination and filters
 */
export function useSOPs(options?: SopListQuery & { page?: number; limit?: number; refetchInterval?: number | false }) {
  const api = useApi();

  return useQuery({
    queryKey: sopKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (options?.departmentId) params.set('departmentId', options.departmentId);
      if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
      if (options?.search) params.set('search', options.search);
      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));

      const queryString = params.toString();
      const endpoint = queryString ? `/api/sops?${queryString}` : '/api/sops';

      return api.get<PaginatedResponse<SopListItem>>(endpoint);
    },
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Fetch lightweight SOPs list for dropdowns
 */
export function useSopLookup() {
  const api = useApi();

  return useQuery({
    queryKey: sopKeys.lookup(),
    queryFn: () => api.get<SopLookup[]>('/api/sops/lookup'),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

/**
 * Fetch a SOP's Jobs (DB: sections) for the Templates page's Job-scope picker.
 *
 * Phase 36.4.2 added this endpoint at requireRole('manager') specifically because
 * the Templates page is manager-level while GET /sops/:id is trainer-level — a
 * manager building a template would 403 on useSop().
 *
 * No staleTime, unlike useSopLookup(): a SOP reprocess deletes and recreates
 * every section with fresh UUIDs, and a cached list would hand the picker ids
 * that no longer exist. Refetching on mount is cheap and the picker only opens
 * inside a modal.
 */
export function useSopSectionsLookup(sopId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: sopKeys.sectionsLookup(sopId ?? ''),
    queryFn: () => api.get<SopSectionLookupItem[]>(`/api/sops/${sopId}/sections/lookup`),
    enabled: !!sopId,
  });
}

/**
 * Fetch single SOP details with steps
 */
export function useSop(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: sopKeys.detail(id ?? ''),
    queryFn: () => api.get<SopDetailWithUrl>(`/api/sops/${id}`),
    enabled: !!id,
  });
}

// ============================================
// CRUD MUTATIONS
// ============================================

/**
 * Create SOP and get presigned upload URL
 */
export function useCreateSop() {
  const api = useApi();

  return useMutation({
    mutationFn: (data: CreateSop) =>
      api.post<SopUploadResponse>('/api/sops', data),
    // Don't invalidate immediately - wait for upload completion
  });
}

/**
 * Update SOP metadata
 */
export function useUpdateSop() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSop }) =>
      api.put<SopDetail>(`/api/sops/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sopKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sopKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: sopKeys.lookup() });
    },
  });
}

/**
 * Update SOP steps (bulk replace)
 */
export function useUpdateSopSteps() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSopSteps }) =>
      api.put<SopStepInput[]>(`/api/sops/${id}/steps`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sopKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: sopKeys.lists() });
    },
  });
}

/**
 * Delete SOP (soft delete)
 */
export function useDeleteSop() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/sops/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sopKeys.all });
    },
  });
}

// ============================================
// AI PARSING (Legacy - deprecated)
// ============================================

/**
 * Parse SOP with Gemini AI (Legacy endpoint)
 * @deprecated Use useProcessSop for the new async pipeline
 */
export function useParseSop() {
  const api = useApi();

  return useMutation({
    mutationFn: ({ id, forceReparse = false }: { id: string; forceReparse?: boolean }) =>
      api.post<ParseSopResponse>(`/api/sops/${id}/parse`, { forceReparse }),
  });
}

// ============================================
// SOP PROCESSING PIPELINE (New async pipeline)
// ============================================

/**
 * Start async SOP processing with the new multi-stage pipeline
 * Returns immediately with a jobId - use useSopProcessingStatus to poll for progress
 */
export function useProcessSop() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, forceReprocess = false }: { id: string; forceReprocess?: boolean }) =>
      api.post<SopProcessingJobResponse>(`/api/sops/${id}/process`, { forceReprocess }),
    onSuccess: (_, variables) => {
      // Invalidate processing status to start fresh
      queryClient.invalidateQueries({ queryKey: sopKeys.processingStatus(variables.id) });
    },
  });
}

/**
 * Poll for SOP processing status
 * Automatically refetches every 2 seconds while processing is active
 */
export function useSopProcessingStatus(sopId: string | null, enabled: boolean = true) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: sopKeys.processingStatus(sopId ?? ''),
    queryFn: () => api.get<SopProcessingStatusResponse>(`/api/sops/${sopId}/process`),
    enabled: !!sopId && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling when completed or failed
      if (data?.status === 'completed' || data?.status === 'failed') {
        // Invalidate SOP detail to refresh with new data
        queryClient.invalidateQueries({ queryKey: sopKeys.detail(sopId!) });
        queryClient.invalidateQueries({ queryKey: sopKeys.lists() });
        return false;
      }
      // Poll every 2 seconds while processing
      return 2000;
    },
    staleTime: 1000, // Consider stale after 1 second
  });
}

// ============================================
// UTILITY: Upload PDF to S3
// ============================================

/**
 * Upload a PDF file directly to S3 using presigned URL
 */
export async function uploadPdfToS3(uploadUrl: string, file: File): Promise<boolean> {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/pdf',
      },
    });

    return response.ok;
  } catch (error) {
    console.error('S3 PDF upload failed:', error);
    return false;
  }
}

/**
 * Refresh SOP data after upload completes
 */
export function useRefreshSop() {
  const queryClient = useQueryClient();

  return (sopId: string) => {
    queryClient.invalidateQueries({ queryKey: sopKeys.detail(sopId) });
    queryClient.invalidateQueries({ queryKey: sopKeys.lists() });
  };
}

// ============================================
// AI SOP BUILDER (Phase 24.2)
// ============================================

/**
 * AI conversation for generating SOP structure.
 * Stateless: sends full message history, returns next question or final SOP structure.
 * Uses the existing assessment-schedules conversation endpoint with templateCode 'general'.
 */
export function useSopConversation() {
  const api = useApi();
  return useMutation({
    mutationFn: (data: { templateCode: string; messages: Array<{ role: 'ai' | 'user'; text: string }> }) =>
      api.post<{
        done: boolean;
        question?: string;
        questionIndex?: number;
        totalQuestions?: number;
        sopStructure?: Record<string, unknown>;
        message?: string;
      }>('/api/assessment-schedules/conversation', data),
  });
}

/**
 * Persist AI-generated SOP structure to the database.
 * Returns the new SOP id and title.
 */
export function useGenerateSop() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ sopId: string; title: string }>('/api/assessment-schedules/generate-sop', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sopKeys.all });
    },
  });
}
